import { DOMAttributes, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { CopyToClipboard } from "react-copy-to-clipboard";
import useStore from "@store/store";
import { selectArchiveDataActions } from "@store/slices/archiveSlice";
import { Query } from "../../utils/types";
import {
  selectAccountData,
  selectAccountDataActions,
  selectOldStripeData,
} from "@store/slices/accountSlice";
import { useUser } from "../../utils/AuthContext";
import Button from "@components/Utils/Button";
import InstallAddonCta from "@components/Utils/InstallAddonCta";
import EmailVerificationCta from "@components/Utils/EmailVerificationCta";
import ResubscribeCta from "@components/Utils/ResubscribeCta";
import { EXCEL_BOT_TOKEN } from "../../utils/constants";

const QueryBuilder = () => {
  const { user } = useUser();
  const accountData: any = useStore(selectAccountData);
  const oldStripeData = useStore(selectOldStripeData);
  const { addQuery, updateQueryFeedback } = useStore(selectArchiveDataActions);
  const { setCreditBalance } = useStore(selectAccountDataActions);

  const [currentQuery, setCurrentQuery] = useState<Query>();
  const [generate, setGenerate] = useState<boolean>(true);
  const [explain, setExplain] = useState<boolean>(false);
  const [excel, setExcel] = useState<boolean>(true);
  const [sheet, setSheet] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [loadingFeedback, setLoadingFeedback] = useState<boolean>(false);
  const [feedbackRegistered, setFeedbackRegistered] = useState<boolean>(false);
  const [response, setResponse] = useState("");

  const placeholder = useMemo(() => {
    if (explain)
      return '=IF(AND(B1>100,REGEXMATCH(C1,"^hello.*goodbye$")),REGEXEXTRACT(A1,"w+sw+sw+s(w+)"),)';

    if (generate)
      return 'Extract the fourth word in A1 only when cell B1 is greater than 100 and when C1 starts with the word "hello" and ends with the word "goodbye".';
  }, [generate, explain]);

  const output = useMemo(() => {
    if (generate)
      return '=IF(AND(B1>100,REGEXMATCH(C1,"^hello.*goodbye$")),REGEXEXTRACT(A1,"w+sw+sw+s(w+)"),)';

    if (explain)
      return 'Extract the fourth word in A1 only when cell B1 is greater than 100 and when C1 starts with the word "hello" and ends with the word "goodbye".';
  }, [generate, explain]);

  useEffect(() => {
    let intervalId: NodeJS.Timer;
    if (copied) {
      intervalId = setInterval(() => {
        setCopied(false);
      }, 1000);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [copied]);

  const handleSubmit: DOMAttributes<HTMLFormElement>["onSubmit"] = async (
    e: any
  ) => {
    e.preventDefault();

    if (accountData?.creditBalance <= 0 && accountData?.isPremium !== true) {
      alert(
        "You have used your monthly AI credits. Upgrade to Pro and get unlimited access!"
      );
      return;
    }

    const input: string = e.target.formula.value;
    if (explain && input.charAt(0) !== "=") {
      alert("Please provide a valid formula.");
      return;
    }

    setLoading(true);
    setCopied(false);
    setResponse("");
    setCurrentQuery(undefined);
    setFeedbackRegistered(false);

    let res: any;
    if (generate) {
      res = await axios.post(
        "/api/formula",
        {
          query: {
            id: uuidv4(),
            prompt: input,
            operation: "Generate",
            type: excel ? "Excel" : "Google Sheets",
            isCorrect: null,
          },
          uid: accountData?.uid,
          creditBalance: !accountData?.isPremium
            ? accountData?.creditBalance - 1
            : accountData?.creditBalance,
          creditMax: 5,
        },
        {
          headers: {
            // @ts-ignore
            Authorization: `Bearer ${user.accessToken}`,
            ExcelBotToken: EXCEL_BOT_TOKEN,
          },
        }
      );
    } else {
      res = await axios.post(
        "/api/explain",
        {
          query: {
            id: uuidv4(),
            prompt: input,
            operation: "Explain",
            type: excel ? "Excel" : "Google Sheets",
            isCorrect: null,
          },
          uid: accountData?.uid,
          creditBalance: !accountData?.isPremium
            ? accountData?.creditBalance - 1
            : accountData?.creditBalance,
          creditMax: 5,
        },
        {
          headers: {
            // @ts-ignore
            Authorization: `Bearer ${user.accessToken}`,
            ExcelBotToken: EXCEL_BOT_TOKEN,
          },
        }
      );
    }

    if (res.status === 200) {
      window.gtag(
        'event',
        'query_prompt', {
        'prompt': input
      }
      )
      addQuery(res.data);
      setCurrentQuery(res.data);
      setResponse(res.data.response);
      if (!accountData?.isPremium) {
        setCreditBalance(accountData?.creditBalance - 1);
      }
    } else {
      alert("Error generating formula - " + JSON.stringify(res.data));
    }

    setLoading(false);
  };

  const handleFeedback = async (isCorrect: boolean) => {
    setLoadingFeedback(true);
    const res = await axios.post(
      "/api/feedback",
      {
        uid: accountData?.uid,
        id: currentQuery?.id,
        operation: currentQuery?.operation,
        isCorrect,
        type: "Formula",
      },
      {
        headers: {
          // @ts-ignore
          Authorization: `Bearer ${user.accessToken}`,
          ExcelBotToken: EXCEL_BOT_TOKEN,
        },
      }
    );

    if (res.status === 200) {
      updateQueryFeedback(currentQuery?.id as string, isCorrect);
      setCurrentQuery({ ...currentQuery, isCorrect } as Query);
      setFeedbackRegistered(true);
    } else {
      alert("Error registering feedback");
    }

    setLoadingFeedback(false);
  };

  return (
    <div className="pt-5 pb-10 w-full h-full lg:scrollbar-thin lg:scrollbar-thumb-gray-500 lg:overflow-y-scroll lg:scrollbar-thumb-rounded-full lg:scrollbar-track-rounded-full">
      <h1 className="pl-8 pb-2 text-3xl font-bold border-b-2">
        Configure Request
      </h1>

      {oldStripeData && <ResubscribeCta />}

      <div className="page-content-wrapper">
        <div className="w-full">
          {!user?.emailVerified ? (
            <EmailVerificationCta />
          ) : (
            <div>
              <form onSubmit={handleSubmit}>
                <p className="text-sm font-medium text-gray-500 mb-2 mt-10">
                  Is this request for Excel or Google Sheets?
                </p>

                <ul className="grid gap-6 max-w-md md:grid-cols-2">
                  <li>
                    <input
                      checked={excel}
                      onChange={(e) => {
                        setSheet(!sheet);
                        setExcel(!excel);
                      }}
                      type="radio"
                      id="excel"
                      name="excel"
                      value="excel"
                      className="hidden peer"
                    />
                    <label
                      htmlFor="excel"
                      className="duration-300 inline-flex items-center justify-between w-full px-5 py-2 text-gray-500 bg-white border border-gray-200 rounded-xl cursor-pointer peer-checked:border-green-600 peer-checked:text-green-600 peer-checked:bg-green-200 hover:text-green-600 hover:bg-green-100"
                    >
                      <div className="w-full text-lg font-semibold">Excel</div>
                    </label>
                  </li>
                  <li>
                    <input
                      checked={sheet}
                      onChange={(e) => {
                        setSheet(!sheet);
                        setExcel(!excel);
                      }}
                      type="radio"
                      id="sheet"
                      name="sheet"
                      value="sheet"
                      className="hidden peer"
                    />
                    <label
                      htmlFor="sheet"
                      className="duration-300 inline-flex items-center justify-between w-full px-5 py-2 text-gray-500 bg-white border border-gray-200 rounded-xl cursor-pointer peer-checked:border-green-600 peer-checked:text-green-600 peer-checked:bg-green-200 hover:text-green-600 hover:bg-green-100"
                    >
                      <div className="w-full text-lg font-semibold">
                        Google Sheets
                      </div>
                    </label>
                  </li>
                </ul>

                <p className="text-sm font-medium text-gray-500 mb-2 mt-10">
                  Do you want to generate a formula or have a formula explained?
                </p>

                <ul className="grid gap-6 max-w-md md:grid-cols-2">
                  <li>
                    <input
                      checked={generate}
                      onChange={(e) => {
                        setGenerate(!generate);
                        setExplain(!explain);
                      }}
                      type="radio"
                      id="generate"
                      name="generate"
                      value="generate"
                      className="hidden peer"
                    />
                    <label
                      htmlFor="generate"
                      className="duration-300 inline-flex items-center justify-between w-full px-5 py-2 text-gray-500 bg-white border border-gray-200 rounded-xl cursor-pointer peer-checked:border-green-600 peer-checked:text-green-600 peer-checked:bg-green-200 hover:text-green-600 hover:bg-green-100"
                    >
                      <div className="w-full text-lg font-semibold">Generate</div>
                    </label>
                  </li>
                  <li>
                    <input
                      checked={explain}
                      onChange={(e) => {
                        setGenerate(!generate);
                        setExplain(!explain);
                      }}
                      type="radio"
                      id="explain"
                      name="explain"
                      value="explain"
                      className="hidden peer"
                    />
                    <label
                      htmlFor="explain"
                      className="duration-300 inline-flex items-center justify-between w-full px-5 py-2 text-gray-500 bg-white border border-gray-200 rounded-xl cursor-pointer peer-checked:border-green-600 peer-checked:text-green-600 peer-checked:bg-green-200 hover:text-green-600 hover:bg-green-100"
                    >
                      <div className="w-full text-lg font-semibold">Explain</div>
                    </label>
                  </li>
                </ul>

                <p className="text-sm font-medium text-gray-500 mb-2 mt-10">
                  Type the problem you are trying to solve or formula you want
                  explained. Be as specific as possible
                </p>
                <div className="form-control mb-5">
                  <textarea
                    className="textarea textarea-bordered rounded-xl w-3/4 h-24 ml-2"
                    placeholder={placeholder}
                    name="formula"
                    required
                  ></textarea>
                </div>

                <Button
                  type="submit"
                  loading={loading}
                  loadingText={"Running AI..."}
                >
                  Get {generate ? "Formula" : "Explanation"}
                </Button>
              </form>

              <p className="text-sm font-medium text-gray-500 mb-2 mt-10">
                Output
              </p>

              <textarea
                className="textarea textarea-bordered rounded-xl w-3/4 h-24 ml-2 whitespace-pre-wrap"
                placeholder={output}
                name="output"
                readOnly
                value={response}
                required
              ></textarea>

              {response && (
                <>
                  <div className="mb-4">
                    <CopyToClipboard
                      text={response}
                      onCopy={() => setCopied(true)}
                    >
                      <button
                        type="button"
                        className={`flex items-center gap-2 p-2 mt-3 border rounded-xl ${copied ? "bg-green-300" : "bg-white"
                          }`}
                      >
                        {copied ? (
                          <>
                            <span className="material-icons">done</span>
                            Copied
                          </>
                        ) : (
                          <>
                            <span className="material-icons">content_copy</span>
                            Copy
                          </>
                        )}{" "}
                      </button>
                    </CopyToClipboard>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2 mt-2">
                      Is the output correct?
                    </p>
                    <div className="flex gap-4">
                      <button
                        className={`btn btn-sm btn-square btn-success ${loadingFeedback && "loading"
                          }`}
                        onClick={() => handleFeedback(true)}
                        disabled={feedbackRegistered}
                      >
                        {!loadingFeedback && (
                          <span className="material-icons">thumb_up</span>
                        )}
                      </button>
                      <button
                        className={`btn btn-sm btn-square btn-error ${loadingFeedback && "loading"
                          }`}
                        onClick={() => handleFeedback(false)}
                        disabled={feedbackRegistered}
                      >
                        {!loadingFeedback && (
                          <span className="material-icons">thumb_down</span>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {user?.emailVerified && (
          <div className="mt-8 mb-6">
            <InstallAddonCta view="vertical" />
          </div>
        )}
      </div>

      <div className="min-h-[5rem] md:hidden"></div>
    </div>
  );
};

export default QueryBuilder;
